import {
  BillingElementsOptions,
  BillingElementsStatus,
  CHECKOUT_MESSAGES,
  CheckoutMessagePayload,
  CheckoutMessageType,
  ELEMENT_STATUSES,
  ELEMENT_TYPES,
  ElementType,
} from './types'
import { createIframe } from './utils/create-iframe'
import { submitFormToTarget } from './utils/form-submitter'

let instanceCounter = 0

const MESSAGE_HANDLER_OPTION_KEYS: Record<CheckoutMessageType, keyof BillingElementsOptions> = {
  [CHECKOUT_MESSAGES.LOADED]: 'onLoaded',
  [CHECKOUT_MESSAGES.ERROR]: 'onError',
  [CHECKOUT_MESSAGES.BACK]: 'onBack',
  [CHECKOUT_MESSAGES.CLOSE]: 'onClose',
  [CHECKOUT_MESSAGES.PAYMENT_SUCCESS]: 'onPaymentSuccess',
  [CHECKOUT_MESSAGES.PAYMENT_PROCESSING]: 'onPaymentProcessing',
  [CHECKOUT_MESSAGES.PAYMENT_ERROR]: 'onPaymentError',
}

const VALID_ELEMENT_TYPES: ElementType[] = Object.values(ELEMENT_TYPES)

type MessageHandler = (data?: CheckoutMessagePayload) => void

export class BillingElements {
  private readonly elementType: ElementType
  private readonly options: BillingElementsOptions
  readonly iframeId: string
  private iframe: HTMLIFrameElement | null = null
  private windowMessageListener: ((event: MessageEvent) => void) | null = null
  private statusValue: BillingElementsStatus = ELEMENT_STATUSES.IDLE
  private handlers: Map<CheckoutMessageType, Set<MessageHandler>> = new Map()

  constructor(elementType: ElementType, options: BillingElementsOptions) {
    if (!VALID_ELEMENT_TYPES.includes(elementType)) {
      throw new Error(
        `Invalid element type: "${elementType}". Must be one of: ${VALID_ELEMENT_TYPES.join(', ')}`
      )
    }

    this.elementType = elementType
    this.options = options
    this.iframeId = `billing-elements-${++instanceCounter}`
    this.setupMessageListener()
  }

  get status(): BillingElementsStatus {
    return this.statusValue
  }

  get type(): ElementType {
    return this.elementType
  }

  destroy(): void {
    if (this.statusValue === ELEMENT_STATUSES.DESTROYED) return

    this.removeWindowMessageListener()
    this.handlers.clear()

    if (this.iframe) {
      this.iframe.remove()
      this.iframe = null
    }

    this.statusValue = ELEMENT_STATUSES.DESTROYED
  }

  on(messageType: CheckoutMessageType, handler: MessageHandler): () => void {
    let handlersForType = this.handlers.get(messageType)
    if (!handlersForType) {
      handlersForType = new Set()
      this.handlers.set(messageType, handlersForType)
    }
    handlersForType.add(handler)

    return () => {
      this.handlers.get(messageType)?.delete(handler)
    }
  }

  mount(container: HTMLElement): void {
    if (this.iframe) {
      throw new Error('BillingElements: iframe already exists. Call destroy() first.')
    }
    this.iframe = createIframe(container, this.iframeId)

    const options = this.options
    const url = new URL(options._src ?? `/${options.route}`, window.location.origin)

    Object.entries(options.queryParams ?? {}).forEach(([k, v]) => url.searchParams.set(k, v))
    url.searchParams.set('embedded', 'true')

    const action = url.toString()
    const data = options.checkoutData ?? {}

    submitFormToTarget({ action, method: 'POST', target: this.iframeId, data })
  }

  postMessage({ action, data }: { action: string; data: unknown }) {
    this.iframe?.contentWindow?.postMessage(
      {
        action,
        data,
      },
      '*'
    )
  }

  private setupMessageListener(): void {
    if (this.windowMessageListener) return
    this.windowMessageListener = (event: MessageEvent) => {
      const isFromOtherInstance = event.source !== this.iframe?.contentWindow
      if (isFromOtherInstance) return
      const messageType = event.data?.type as CheckoutMessageType
      if (!messageType?.startsWith('checkout:')) return

      const payload = event.data?.data

      const optionKey = MESSAGE_HANDLER_OPTION_KEYS[messageType]
      if (optionKey) {
        const optionHandler = this.options[optionKey]
        if (typeof optionHandler === 'function') {
          optionHandler(payload)
        }
      }

      const handlers = this.handlers.get(messageType)
      if (handlers) {
        ;[...handlers].forEach((handler) => handler(payload))
      }

      if (messageType === CHECKOUT_MESSAGES.LOADED) {
        this.statusValue = ELEMENT_STATUSES.READY
      }
    }

    window.addEventListener('message', this.windowMessageListener)
  }

  private removeWindowMessageListener(): void {
    if (this.windowMessageListener) {
      window.removeEventListener('message', this.windowMessageListener)
      this.windowMessageListener = null
    }
  }
}
